import React, { useState } from "react";
import ReportPageShell from "../components/ReportPageShell";
import MetricGrid from "../components/MetricGrid";
import StatCard from "../components/StatCard";
import { useReportQuery } from "../shared/useReportQuery";
import { useSearchGate } from "../shared/useSearchGate";
import { defaultFilters, toQueryParams } from "../shared/filters";
import { centsToUsd, formatCount } from "../shared/format";
import { MetricKey } from "../api/types";

const TOOLTIP =
  "Discounts & Promo Codes from the new engine — one row per whole-order discount/promo: how many " +
  "times it was applied (Times Used) and the dollar total. The Code / Type column splits customer promo " +
  "codes ('code: XXXX') from admin-applied discounts, with the value kind (· % or · $).";

// Column-header overrides for this report's semantics (OrderCount here = application count, not distinct orders).
const LABEL_OVERRIDES: Partial<Record<MetricKey, string>> = {
  OrderCount: "Times Used",
  Discount: "Discount $",
};

const CSV_LEGEND = [
  "",
  "Times Used = number of times the discount/promo was applied.",
  "Discount $ = total dollars discounted (Σ applied amount).",
  "Code / Type: 'code: XXXX' = customer promo code; 'admin-applied' = admin discount; '· %' / '· $' = value kind.",
  "SCOPE: whole-order discounts only — line-item discounts aren't attributable to a named discount/promo.",
];

// Discounts + Promo Codes (RP-2160/2161). DiscountUsage: dimension=None, metrics [OrderCount, Discount],
// row.group carries the customer-promo vs admin-discount split (+ value kind).
export default function DiscountUsageReport() {
  const [filters, setFilters] = useState(defaultFilters);
  const { submitted, isDirty, hasRun, search } = useSearchGate({ filters });
  const state = useReportQuery(
    submitted ? toQueryParams("DiscountUsage", submitted.filters) : null
  );

  return (
    <ReportPageShell
      title="Discounts & Promo Codes"
      tooltipText={TOOLTIP}
      filters={filters}
      onFiltersChange={setFilters}
      state={state}
      onSearch={search}
      isDirty={isDirty}
      hasRun={hasRun}
    >
      {(data) => (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3">
            <StatCard label="Total discount $" value={centsToUsd(data.totals.Discount)} emphasis />
            <StatCard label="Total times used" value={formatCount(data.totals.OrderCount)} />
          </div>
          <div className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-500">
            Whole-order discounts only — line-item discounts aren't linked to a named discount/promo, so they're
            not attributable here (raised with Marc separately).
          </div>
          <MetricGrid
            data={data}
            keyColumnHeader="Discount / Promo"
            groupColumnHeader="Code / Type"
            metricLabelOverrides={LABEL_OVERRIDES}
            csvExtra={CSV_LEGEND}
            exportName="DiscountUsage"
          />
        </>
      )}
    </ReportPageShell>
  );
}
